import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";

export enum ViewPortSize {
  Mobile,
  Tablet,
  Desktop,
}

export interface GlobalStateInterface {
  viewPortSize: ViewPortSize;
}

const GlobalStateContext = createContext({
  state: {} as Partial<GlobalStateInterface>,
  setState: {} as Dispatch<SetStateAction<Partial<GlobalStateInterface>>>,
});

const GlobalStateProvider = ({
  children,
  value = {} as GlobalStateInterface,
}: {
  children: React.ReactNode;
  value?: Partial<GlobalStateInterface>;
}) => {
  const [state, setState] = useState(value);

  useEffect(() => {
    const setResponsiveness = () =>
      setState((prev) => ({
        ...prev,
        viewPortSize:
          window.innerWidth < 600
            ? ViewPortSize.Mobile
            : window.innerWidth <= 1024
            ? ViewPortSize.Tablet
            : ViewPortSize.Desktop,
      }));

    setResponsiveness();
    window.addEventListener("resize", () => setResponsiveness());

    return () => {
      window.removeEventListener("resize", () => setResponsiveness());
    };
  }, [state.viewPortSize]);

  return (
    <GlobalStateContext.Provider value={{ state, setState }}>
      {children}
    </GlobalStateContext.Provider>
  );
};

const useGlobalState = () => {
  const context = useContext(GlobalStateContext);
  if (!context) {
    throw new Error("useGlobalState must be used within a GlobalStateContext");
  }
  return context;
};

export { GlobalStateProvider, useGlobalState };
